/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { ImpulseElement, property, registerElement, target } from '@ambiki/impulse';
import trimCanvas from 'helpers/trim_canvas';
import { fitFactor, scaleStrokes } from 'helpers/signature_strokes';
import SignaturePad from 'signature_pad';

type Strokes = ReturnType<SignaturePad['toData']>;

@registerElement('signature-pad')
export default class SignaturePadElement extends ImpulseElement {
  /**
   * Whether the signature value is required or not.
   */
  @property({ type: Boolean }) required = false;

  /**
   * Whether free-hand drawing is disabled or not.
   */
  @property({ type: Boolean }) drawable = true;

  /**
   * Whether to trim transparent edges from the signature before saving.
   */
  @property({ type: Boolean }) trim = true;

  @target() input: HTMLInputElement;
  @target() hiddenField?: HTMLInputElement;

  private signaturePad: SignaturePad;
  private abortController = new AbortController();

  /**
   * The hand-drawn strokes exactly as they were drawn. Null while nothing has been drawn. Redraws
   * scale a copy of this to fit rather than editing it in place, so a pad that narrows and then
   * widens comes back at full size instead of compounding a shrink every time the device is
   * rotated.
   */
  private drawn: Strokes | null = null;

  /**
   * The devicePixelRatio the context is currently scaled for. Starts at 0 so the first resize
   * always runs. The no-op check in onWindowResize cannot rely on the backing-store size alone:
   * browser zoom shrinks the CSS box by exactly the amount the ratio grows, so offsetWidth * ratio
   * can come out unchanged while every coordinate now means something different — skipping the
   * re-scale there leaves the ink landing offset from the pen.
   */
  private appliedRatio = 0;

  /**
   * The exact string setText last rendered onto the canvas, '' when the pad holds no typed text.
   * The resize re-render replays this instead of re-deriving text from the input: the input can
   * hold less than what is on the canvas (the staff signature page composes "name + credential"
   * into setText while the input keeps the bare name) or something that is not text at all
   * (drawn-only pads seed the input with a saved data URL).
   */
  private lastRenderedText = '';

  /**
   * Whether a stroke is currently being drawn, and whether a resize arrived while it was. The
   * resize work must never run mid-stroke: the library is still appending points to its last
   * group, so clearing and repopulating the canvas welds the rest of the stroke onto the restored
   * signature. A deferred resize runs from onEndStroke once the stroke has been captured.
   */
  private strokeInProgress = false;
  private resizePending = false;

  connected() {
    const { signal } = this.abortController;

    this.setCanvasDrawCapability(this.drawable);
    this.calculateDimensions();
    this.canvas.style.padding = '0';
    this.signaturePad = new SignaturePad(this.canvas, { minWidth: 1, maxWidth: 1 });
    this.signaturePad.addEventListener('beginStroke', this.onBeginStroke.bind(this), { signal });
    this.signaturePad.addEventListener('endStroke', this.onEndStroke.bind(this), { signal });
    window.addEventListener('resize', this.onWindowResize.bind(this), { signal });
    this.setCustomValidity(this.required);
  }

  disconnected() {
    this.clear();
    this.signaturePad.off();
    this.abortController.abort();
  }

  drawableChanged(value: boolean) {
    this.setCanvasDrawCapability(value);
  }

  // Force SeptemberMornings font to load
  inputConnected() {
    if (this.ctx) {
      this.ctx.font = 'normal normal normal 4.5rem SeptemberMornings';
      this.ctx.fillText('', 0, 0);
    }
  }

  requiredChanged(value: boolean) {
    this.setCustomValidity(value);
  }

  type(event: Event) {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || !this.ctx) return;

    this.setText(this.input.value);
  }

  setText(text: string) {
    // Typing (or composing text into the pad) replaces the signature. setText's clearRect already
    // erases any hand-drawn ink from view, so the stroke state has to go with it — otherwise the
    // next real resize would paint the abandoned strokes back over the typed name and save the
    // composite. Dropping the pad's own data too means a stroke drawn after typing starts clean.
    if (this.signaturePad) {
      this.signaturePad.clear();
    }
    this.drawn = null;
    this.lastRenderedText = text.trim();
    this.renderText(this.lastRenderedText);
  }

  private renderText(text: string) {
    if (!this.ctx) return;

    // Draw in CSS pixels, not backing-store pixels. onWindowResize() scales the context by the
    // device pixel ratio, so canvas.width/height (the backing store) are `ratio` times larger than
    // the coordinate space we're drawing in — using them here would put the text off the canvas.
    const { width, height } = this.logicalSize;

    this.ctx.font = 'normal normal normal 5.5rem SeptemberMornings';
    this.ctx.clearRect(0, 0, width, height);

    const xAxis = width / 2;
    const yAxis = height / 2 + 12; // Add some offset to account for text height.

    this.ctx.textAlign = 'center';
    this.ctx.fillText(text, xAxis, yAxis, width);

    if (text.length === 0) {
      this.updateHiddenField('');
    } else {
      this.updateHiddenField();
    }
  }

  clear() {
    this.signaturePad.clear();
    this.drawn = null;
    this.lastRenderedText = '';
    this.input.value = '';
    this.updateHiddenField('');
    this.emit('clear');
  }

  calculateDimensions() {
    const { width, height } = this.canvas.parentElement!.getBoundingClientRect();
    this.canvas.setAttribute('width', `${width}px`);
    this.canvas.setAttribute('height', `${height}px`);
    this.canvas.style.height = `${height}px`;
  }

  private onBeginStroke() {
    this.strokeInProgress = true;
  }

  private onEndStroke() {
    this.strokeInProgress = false;
    this.captureDrawn();
    this.updateHiddenField();
    // A resize deferred while the pen was down runs now that the finished stroke is captured.
    if (this.resizePending) {
      this.resizePending = false;
      this.onWindowResize();
    }
  }

  /**
   * Adopt what is on the pad now as the signature-as-drawn.
   */
  private captureDrawn() {
    const data = this.signaturePad.toData();
    this.drawn = data.length > 0 ? data.slice() : null;
  }

  // Handle the case where the size of the window changes. Otherwise, the mouse pointer will not match
  // the signature line.
  private onWindowResize() {
    if (this.strokeInProgress) {
      this.resizePending = true;
      return;
    }
    // A pad reporting no size is a layout that has not settled (container mid-drag, hidden panel) —
    // and no resize event fires when a display:none container comes back. Zeroing the canvas here
    // breaks the save two ways: trimCanvas throws on a zero-size copy, and the fallback
    // toDataURL() on a 0-wide canvas returns the literal string 'data:,', which would land in the
    // hidden field the form submits. Leave everything alone until there is a real size again.
    if (this.canvas.offsetWidth === 0 || this.canvas.offsetHeight === 0) return;
    const ratio = Math.max(window.devicePixelRatio || 1, 1);
    // Round: canvas.width/height are unsigned longs, so the browser truncates whatever we assign.
    // Most Android phones report a fractional devicePixelRatio (2.625, 2.75, 3.5) and so does any
    // desktop browser at non-100% zoom, so without rounding the value we store never equals the
    // value we compute and the no-op check below can never be true.
    const width = Math.round(this.canvas.offsetWidth * ratio);
    const height = Math.round(this.canvas.offsetHeight * ratio);
    // Mobile browsers fire resize constantly without the canvas box changing size (URL bar
    // collapsing on scroll, soft keyboard opening) — bail out so the signature isn't wiped.
    if (this.canvas.width === width && this.canvas.height === height && this.appliedRatio === ratio) return;
    this.canvas.width = width;
    this.canvas.height = height;
    if (this.ctx) {
      this.ctx.scale(ratio, ratio);
    }
    this.appliedRatio = ratio;
    this.signaturePad.clear();
    if (this.lastRenderedText) {
      // Re-render the typed signature at the new dimensions (also re-syncs the hidden field).
      // Replay the exact string setText last rendered rather than re-deriving it from the input:
      // the staff signature page composes "name + credential" into setText while the input keeps
      // the bare name, and drawn-only pads seed the input with a saved data URL that must never be
      // painted as text. lastRenderedText is only ever set by real setText callers, so it is safe
      // for both.
      this.renderText(this.lastRenderedText);
    }
    if (this.drawn) {
      // Redraw hand-drawn strokes (e.g. after device rotation) over any typed name — the user
      // signature pad lets you type a name and then draw on the same canvas. `clear: false` keeps
      // the typed text; the clear() above already reset the pad's stroke data, and fromData
      // repopulates it so Undo still works after a resize.
      //
      // Scaled down if the pad is now too small to hold the signature: strokes redrawn at their
      // original coordinates would sit outside a narrower canvas, and getSignatureDataURL() copies
      // the canvas as it stands, so the overflow would be cropped out of what we save.
      const { width: padWidth, height: padHeight } = this.logicalSize;
      const strokes = scaleStrokes(this.drawn, fitFactor(this.drawn, padWidth, padHeight));
      this.signaturePad.fromData(strokes, { clear: false });
      this.updateHiddenField();
    }
  }

  /**
   * The canvas size in CSS pixels — the coordinate space drawing commands use once the context has
   * been scaled by the device pixel ratio. Falls back to the backing store for a canvas that isn't
   * laid out yet (hidden container), where offsetWidth/offsetHeight are 0.
   */
  private get logicalSize() {
    return {
      width: this.canvas.offsetWidth || this.canvas.width,
      height: this.canvas.offsetHeight || this.canvas.height,
    };
  }

  private updateHiddenField(rawValue?: string) {
    const value = rawValue ?? this.getSignatureDataURL();

    this.setCustomValidity(this.required && !value);
    this.emit('signed', { detail: { value } });
    if (this.hiddenField) {
      this.hiddenField.value = value;
    }
  }

  /**
   * Gets the signature as a data URL, optionally trimming transparent edges.
   *
   * @returns The signature as a base64-encoded data URL
   */
  private getSignatureDataURL(): string {
    if (!this.trim) {
      return this.signaturePad.toDataURL();
    }

    // Create a temporary canvas to avoid modifying the original
    const tempCanvas = document.createElement('canvas');
    const tempCtx = tempCanvas.getContext('2d');

    if (!tempCtx) {
      return this.signaturePad.toDataURL();
    }

    // Copy the current canvas content to the temporary canvas
    tempCanvas.width = this.canvas.width;
    tempCanvas.height = this.canvas.height;
    tempCtx.drawImage(this.canvas, 0, 0);

    // Trim the temporary canvas
    try {
      trimCanvas(tempCanvas);
      return tempCanvas.toDataURL();
    } catch (error) {
      return this.signaturePad.toDataURL();
    }
  }

  private setCustomValidity(value: boolean) {
    this.input.setCustomValidity(value ? 'Please type or draw your signature' : '');
  }

  private setCanvasDrawCapability(value: boolean) {
    this.canvas.style.pointerEvents = value ? 'auto' : 'none';
  }

  /**
   * Returns an object that provides methods and properties for drawing and manipulating images and graphics on a
   * canvas element in a document. A context object includes information about colors, line widths, fonts, and other
   * graphic parameters that can be drawn on a canvas.
   */
  get ctx() {
    return this.canvas.getContext('2d');
  }

  get canvas() {
    return this.querySelector('canvas')!;
  }
}
